import { Label, Badge } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { HiX } from "react-icons/hi";
import { District } from "@/sharedTypes";
import { useGetDistrictsQuery } from "@/api/locationApi/district";

interface EventDistrictSelectProps {
  cityId: string | number | undefined;
  value: number[];
  onChange: (value: number[]) => void;
}

// Scoped to the currently selected city — leaving this empty means "the
// whole city" (task spec D4), so there is no "select all" shortcut here.
export default function EventDistrictSelect({ cityId, value, onChange }: EventDistrictSelectProps) {
  const { t } = useTranslation(["temporalActivity"]);
  const { data: districtsData, isFetching } = useGetDistrictsQuery(
    cityId ? { filters: { city_id: cityId }, pagination: { page: 1, pageSize: 100 } } : undefined,
    { skip: !cityId }
  );

  const districts: District[] = districtsData?.items || [];
  const safeValue = Array.isArray(value) ? value : [];

  const handleToggle = (districtId: number) => {
    if (safeValue.includes(districtId)) {
      onChange(safeValue.filter((id) => id !== districtId));
    } else {
      onChange([...safeValue, districtId]);
    }
  };

  const selectedDistricts = districts.filter((d) => safeValue.includes(d.id));
  const availableDistricts = districts.filter((d) => !safeValue.includes(d.id));

  if (!cityId) {
    return (
      <div className="mb-4">
        <Label value={t("temporalActivity:event.districts")} />
        <p className="mt-1 text-sm text-gray-400">{t("temporalActivity:event.selectCityFirst")}</p>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <Label value={t("temporalActivity:event.districts")} />
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {t("temporalActivity:event.districtsHelp")}
      </p>

      {selectedDistricts.length > 0 && (
        <div className="mb-2 mt-2 flex flex-wrap gap-2">
          {selectedDistricts.map((d) => (
            <Badge key={d.id} color="info" className="flex items-center gap-1">
              {d.district_name}
              <button type="button" onClick={() => handleToggle(d.id)} className="ml-1 inline-flex items-center">
                <HiX className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <select
        className="mt-2 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-cyan-500 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-cyan-500 dark:focus:ring-cyan-500"
        value=""
        size={Math.min(availableDistricts.length + 1, 6)}
        onChange={(e) => {
          const id = Number(e.target.value);
          if (id) handleToggle(id);
        }}
        disabled={isFetching || availableDistricts.length === 0}
      >
        <option value="" disabled>
          {availableDistricts.length === 0
            ? t("temporalActivity:event.noMoreDistricts")
            : t("temporalActivity:event.selectDistrict")}
        </option>
        {availableDistricts.map((d) => (
          <option key={d.id} value={d.id}>
            {d.district_name}
          </option>
        ))}
      </select>
    </div>
  );
}
